import AppCard from '@nxsys/components/AppCard';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import type { AppDispatch, AppState } from '@nxsys/store';
import { agencyAction } from '@nxsys/store/slices/agency/reducer/agencySlice';
import { GetAgencyDocumentListParams } from '@nxsys/store/slices/agency/types';
import { appActions } from '@nxsys/store/slices/app/reducer/appSlice';
import { documentActions } from '@nxsys/store/slices/document/reducer/documentSlice';
import {
  DocumentPayload,
  DocumentType,
  GetDocumentParams,
} from '@nxsys/types/models/document';
import { showToast } from '@nxsys/utils/toast';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DocumentAgency from '../../documents/Listing/DocumentList/DocumentAgency';

let timeout: any = null;
const DocumentList = ({
  data,
  hasBorder = true,
}: {
  data: IAgency;
  hasBorder?: boolean;
}) => {
  const agencyId = data.id;
  const { messages } = useTypedIntl();
  const { isAgency } = useSelector((state: AppState) => state.appStore);
  const dispatch = useDispatch<AppDispatch>();
  const documents = useSelector(
    (state: AppState) => state.agencyStore.agencyDocumentList,
  );
  const [filter, setFilter] = useState<GetDocumentParams>({
    page: 0,
    size: 20,
    search: '',
  });

  useEffect(() => {
    handleGetDocuments({
      page: 0,
      size: 20,
    });
  }, []);

  const handleGetDocuments = (params: GetAgencyDocumentListParams) => {
    const newParams: GetAgencyDocumentListParams = {
      ...filter,
      ...params,
    };
    dispatch(
      agencyAction.getAgencyDocumentListById({
        id: agencyId,
        params: newParams,
      }),
    ).unwrap();
    setFilter({ ...newParams });
  };

  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      setFilter((prev) => ({
        ...prev,
        search: value,
      }));
      handleGetDocuments({
        page: 0,
        size: 20,
        search: value,
      });
    }, 500);
  };

  const handleDeleteDocument = async (documentId: string, cb?: () => void) => {
    try {
      await dispatch(
        documentActions.deleteDocument({ id: documentId }),
      ).unwrap();
      showToast({
        type: 'success',
        message: 'Document has been deleted successfully',
      });
      if (cb) cb();
      handleGetDocuments(documents.queryParams as GetAgencyDocumentListParams);
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      showToast({
        type: 'error',
        message:
          error.response.data.message ||
          'Failed to delete the document. Please try again.',
      });
    }
  };

  const handleCreatedDocument = async (values: DocumentPayload) => {
    const params = { ...values };

    try {
      if (values?.documentFile) {
        const { url } = await dispatch(
          appActions.uploadFile({ file: values?.documentFile as File }),
        ).unwrap();
        // NX-6056: the thunk returns the S3 object key; store it as-is.
      params.documentFile = url;
      }
      await dispatch(documentActions.createDocument(params)).unwrap();
      showToast({
        type: 'success',
        message: messages['documents.add.success'],
      });
      handleGetDocuments(documents.queryParams as GetAgencyDocumentListParams);
    } catch (error: any) {
      console.error('Failed to create document:', error);

      showToast({
        type: 'error',
        message:
          error.response?.data?.message || messages['documents.add.error'],
      });
    }
  };

  const handleUpdateDocument = async (
    documentId: string,
    values: DocumentPayload,
    cb?: (document: DocumentType) => void,
  ) => {
    const params = { ...values };
    try {
      const isUploadNewFile = Boolean(
        (values?.documentFile as { name: string; type: string; size: number })
          .name,
      );
      if (isUploadNewFile) {
        const { url } = await dispatch(
          appActions.uploadFile({ file: values?.documentFile as File }),
        ).unwrap();
        // NX-6056: the thunk returns the S3 object key; store it as-is.
      params.documentFile = url;
      }
      const res = await dispatch(
        documentActions.updateDocument({ documentId, params }),
      ).unwrap();
      if (cb) cb(res.data);
      showToast({
        type: 'success',
        message: 'Document has been edited successfully',
      });
      handleGetDocuments(documents.queryParams as GetAgencyDocumentListParams);
    } catch (error: any) {
      console.error('Failed to update document:', error);

      showToast({
        type: 'error',
        message:
          error.response?.data?.message ||
          'Failed to edit the document. Please try again.',
      });
    }
  };

  return (
    <AppCard
      title={hasBorder && !isAgency ? messages['common.document'] : undefined}
      hasBorder={hasBorder}
    >
      <DocumentAgency
        data={{
          list: documents.list,
          pagination: {
            current: documents.pagination.current,
            pageSize: documents.pagination.pageSize,
            total: documents.pagination.totalDocs,
          },
          queryParams: documents.queryParams,
        }}
        loading={documents.loading}
        onGetDocuments={handleGetDocuments as any}
        onDeleteDocument={handleDeleteDocument}
        onCreatedDocument={handleCreatedDocument}
        onUpdateDocument={handleUpdateDocument}
        onChangeSearchTerm={onChangeSearchTerm}
        addNewDefaultValues={{
          agencyRef: {
            label: data.name,
            value: data.id,
          },
        }}
        listDisabled={['agencyRef']}
      />
    </AppCard>
  );
};

export default DocumentList;
